import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ReviewsList } from "@/components/ReviewSystem";
import GetStartedGuide, { shouldShowGuide } from "@/components/GetStartedGuide";
import MobileBottomNav from "@/components/MobileBottomNav";
import { DashboardSkeleton } from "@/components/LoadingSkeleton";
import StaffManagement from "@/components/StaffManagement";
import MiniSiteSettings from "@/components/MiniSiteSettings";
import { UpgradeBanner, UpgradeSidebarCard } from "@/components/UpgradeBanner";
import ReceiptVerification from "@/components/ReceiptVerification";
import PaymentMethodSettings from "@/components/PaymentMethodSettings";
import PrepaymentSettings from "@/components/PrepaymentSettings";
import DisplaySettings from "@/components/DisplaySettings";
import RefundModal from "@/components/RefundModal";
import ReportsDashboard from "@/components/ReportsDashboard";
import {
  Calendar,
  DollarSign,
  Star,
  Clock,
  Scissors,
  LogOut,
  Menu,
  User,
  Users,
  CheckCircle,
  XCircle,
  MapPin,
  Plus,
  Trash2,
  Edit,
  Edit3,
  Camera,
  Share2,
  Instagram,
  Facebook,
  Gift,
  Settings,
  Image as ImageIcon,
  BarChart3,
  Building2,
  Code,
  FileText,
  Check,
  Sparkles,
  ChevronRight,
  Receipt,
  CreditCard,
  Crown,
  Wallet,
  Activity,
  Upload,
  HelpCircle,
  Home,
  TrendingUp,
  UserPlus,
  Inbox,
  UsersRound,
  Globe,
  Zap,
  ArrowRight,
  BookOpen,
  MessageSquare,
  Shield,
  Presentation,
  Palette,
  Heart,
  Lock,
  Copy,
  ChevronDown,
  ArrowLeft,
  Megaphone,
  ExternalLink,
  Percent,
  Download,
  RefreshCw,
  Eye
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import StyleGallery from "@/components/StyleGallery";
import BetaAnalyticsDashboard from "@/components/BetaAnalyticsDashboard";
import ReferralDashboard from "@/components/ReferralDashboard";
import EarningsFeeBreakdown from "@/components/EarningsFeeBreakdown";
import ProfileDropdown from "@/components/ProfileDropdown";
import AIChatbot from "@/components/AIChatbot";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import RefundsDashboard from "@/components/RefundsDashboard";

// Import new components
import RevenueDashboard from "@/components/RevenueDashboard";
import SocialShareCard from "@/components/SocialShareCard";
import PaymentMethodsCard from "@/components/PaymentMethodsCard";
import BoothManagement from "@/components/BoothManagement";
import BookingWidget from "@/components/BookingWidget";
import ClientNotes from "@/components/ClientNotes";
import WaitlistManager from "@/components/WaitlistManager";
import MarketingDashboard from "@/components/MarketingDashboard";
import RewardsCommandCenter from "@/components/RewardsCommandCenter";
import ClientInviteTools from "@/components/ClientInviteTools";
import Mailbox from "@/components/Mailbox";
import AccountingDashboard from "@/components/AccountingDashboard";
import ServiceAddonsManager from "@/components/ServiceAddonsManager";
import ClientManagement from "@/components/ClientManagement";
import StripeConnectCard from "@/components/StripeConnectCard";
import TrialExpiredModal from "@/components/TrialExpiredModal";
import SoldierRecruitmentPlaybook from "@/components/SoldierRecruitmentPlaybook";

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// HairChair Soldiers Navigation Dropdown Component
const SoldierNavDropdown = ({ user, onOpenPlaybook }) => {
  const [isOpen, setIsOpen] = useState(false);
  // Super Admin has unlimited access to all features
  const isSuperAdmin = user?.is_super_admin === true;
  const isSoldier = isSuperAdmin || (user?.is_soldier && user?.soldier_status === 'active');
  
  const soldierLinks = [
    { 
      label: 'Command Center', 
      href: '/soldiers/dashboard', 
      icon: Shield,
      description: 'Your earnings & activity',
      soldierOnly: true
    },
    { 
      label: 'Platform Overview', 
      href: '/demo', 
      icon: Presentation,
      description: 'Full platform demo'
    },
    { 
      label: 'For Clients', 
      href: '/demo?package=client', 
      icon: Heart,
      description: 'Client experience'
    },
    { 
      label: 'For Barbers & Stylists', 
      href: '/demo?package=stylist', 
      icon: Scissors,
      description: 'Stylist tools'
    },
    { 
      label: 'For Salon Owners', 
      href: '/demo?package=owner', 
      icon: Building2,
      description: 'Owner dashboard'
    },
    { 
      label: 'Soldiers Program', 
      href: '/soldiers', 
      icon: Shield,
      description: 'Apply or learn more'
    },
  ];

  return (
    <div className="space-y-1">
      {/* Main Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-amber-400 hover:bg-amber-500/10"
        data-testid="soldier-nav-toggle"
      >
        <Shield className="w-5 h-5" />
        <span className="flex-1 text-left">HairChair Soldiers</span>
        {isSuperAdmin && (
          <Badge className="bg-purple-500/20 text-purple-400 text-xs mr-1">Admin</Badge>
        )}
        {!isSuperAdmin && isSoldier && (
          <Badge className="bg-green-500/20 text-green-400 text-xs mr-1">Active</Badge>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <div className="ml-4 pl-4 border-l border-amber-500/20 space-y-1">
          {/* Recruitment Playbook - Quick Access for Soldiers */}
          {isSoldier && (
            <button
              onClick={onOpenPlaybook}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-300 hover:from-purple-500/30 hover:to-pink-500/30"
              data-testid="soldier-playbook-btn"
            >
              <BookOpen className="w-4 h-4" />
              <span>Recruitment Playbook</span>
              <Badge className="ml-auto bg-purple-500 text-white text-xs">HOT</Badge>
            </button>
          )}
          
          {soldierLinks.map((link) => {
            const isLocked = link.soldierOnly && !isSoldier;
            
            return (
              <Link
                key={link.label}
                to={isLocked ? '/soldiers/dashboard' : link.href}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                  isLocked 
                    ? 'text-stone-600 cursor-not-allowed'
                    : 'text-stone-400 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`soldier-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {isLocked ? (
                  <Lock className="w-4 h-4" />
                ) : (
                  <link.icon className="w-4 h-4" />
                )}
                <span>{link.label}</span>
                {isLocked && (
                  <Badge className="ml-auto bg-stone-800 text-stone-500 text-xs">Locked</Badge>
                )}
                {link.soldierOnly && isSoldier && (
                  <Badge className="ml-auto bg-amber-500 text-black text-xs">NEW</Badge>
                )}
              </Link>
            );
          })}
          
          {/* Share Links Section - Only for Active Soldiers */}
          {isSoldier && (
            <div className="pt-2 mt-2 border-t border-white/5">
              <p className="text-xs text-stone-500 px-3 mb-1">Quick Share</p>
              <button
                onClick={() => {
                  const link = `${window.location.origin}/soldiers?ref=${user.soldier_referral_code}`;
                  navigator.clipboard.writeText(link);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-amber-400 hover:bg-amber-500/10 transition-colors"
              >
                <Copy className="w-4 h-4" />
                <span>Copy Referral Link</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StylistDashboard = () => {
  const { user, token, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [showPlaybook, setShowPlaybook] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [profile, setProfile] = useState(null);
  const [serviceTemplates, setServiceTemplates] = useState({});
  const [customTemplates, setCustomTemplates] = useState({}); // Store customized prices/durations
  const [professionalTypes, setProfessionalTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showGettingStarted, setShowGettingStarted] = useState(true);
  const [showGetStartedGuide, setShowGetStartedGuide] = useState(false);
  const [showUpsellsConfig, setShowUpsellsConfig] = useState(false);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    professional_type: "",
    bio: "",
    experience_years: 0,
    address: "",
    city: "",
    state: "",
    zip_code: "",
    specialties: [],
    is_available: true,
    profile_image: "",
    instagram_handle: "",
    google_business_url: ""
  });
  
  // Profile image upload state
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const profileImageInputRef = useRef(null);
  
  // Service management
  const [showAddService, setShowAddService] = useState(false);
  const [showEditService, setShowEditService] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [editingService, setEditingService] = useState(null);
  const [newService, setNewService] = useState({
    name: "",
    description: "",
    duration_minutes: 30,
    price: 0,
    category: "haircut"
  });
  
  // Schedule state
  const [schedule, setSchedule] = useState(
    DAYS_OF_WEEK.map(day => ({
      day,
      enabled: day !== "Sunday",
      start_time: "09:00",
      end_time: "17:00"
    }))
  );
  
  // Blocked times
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [newBlockedTime, setNewBlockedTime] = useState({
    date: "",
    start_time: "",
    end_time: "",
    reason: ""
  });
  
  // Loyalty config
  const [loyaltyConfig, setLoyaltyConfig] = useState({
    enabled: true,
    points_per_dollar: 1,
    bonus_points_new_client: 50,
    referral_bonus: 100
  });
  
  // Stripe Connect status - shared across tabs
  const [stripeConnectStatus, setStripeConnectStatus] = useState(null);

  // Refund modal state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedAppointmentForRefund, setSelectedAppointmentForRefund] = useState(null);

  // Trial expiration state
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  const [trialChecked, setTrialChecked] = useState(false);

  useEffect(() => {
    fetchData();
    fetchStripeConnectStatus();
    checkTrialStatus();
  }, [token]);
  
  // Check trial status on mount
  const checkTrialStatus = async () => {
    if (!token || trialChecked) return;
    
    try {
      const res = await axios.get(`${API}/membership/trial/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If user was on trial and it expired, show modal
      if (res.data.trial_expired || res.data.trial_exhausted) {
        setShowTrialExpiredModal(true);
      }
      setTrialChecked(true);
    } catch (error) {
      console.log("Trial status check:", error.response?.status);
      setTrialChecked(true);
    }
  };

  // Handle trial expired modal close - downgrade to free
  const handleTrialExpiredContinueFree = async () => {
    try {
      // Update local user state to reflect free tier
      if (user) {
        const updatedUser = {
          ...user,
          membership: { ...user.membership, tier: 'free', is_trial: false }
        };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
      setShowTrialExpiredModal(false);
      toast.info("You're now on the free plan. Upgrade anytime to access premium features.");
    } catch (error) {
      console.error("Error handling trial expiration:", error);
    }
  };

  // Handle URL params for direct navigation (e.g., ?tab=services&upsell=configure)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const upsell = params.get('upsell');
    
    if (tab) {
      setActiveTab(tab);
    }
    if (upsell === 'configure') {
      setShowUpsellsConfig(true);
      // Clear the URL param after handling
      navigate('/dashboard', { replace: true });
    }
  }, [location.search, navigate]);

  // Listen for tab navigation events from child components
  useEffect(() => {
    const handleNavigateTab = (e) => {
      if (e.detail && typeof e.detail === 'string') {
        setActiveTab(e.detail);
      }
    };
    window.addEventListener('navigate-tab', handleNavigateTab);
    return () => window.removeEventListener('navigate-tab', handleNavigateTab);
  }, []);

  // Show Get Started Guide for new users
  useEffect(() => {
    if (user) {
      const userRole = user?.role === 'owner' ? 'owner' : 'stylist';
      if (shouldShowGuide(userRole)) {
        const timer = setTimeout(() => setShowGetStartedGuide(true), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [user]);

  const fetchStripeConnectStatus = async () => {
    try {
      const response = await axios.get(`${API}/stylist/stripe-connect/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeConnectStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch Stripe Connect status:", error);
      setStripeConnectStatus({ connected: false, payouts_enabled: false });
    }
  };

  const fetchData = async () => {
    try {
      const [aptRes, profileRes, templatesRes, typesRes] = await Promise.all([
        axios.get(`${API}/appointments/my`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/stylists/my-profile`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services/templates`),
        axios.get(`${API}/services/professional-types`)
      ]);
      
      setAppointments(aptRes.data);
      setProfile(profileRes.data);
      setServiceTemplates(templatesRes.data);
      setProfessionalTypes(typesRes.data.types);
      
      // Populate form with existing profile data
      if (profileRes.data) {
        setProfileForm({
          professional_type: profileRes.data.professional_type || "",
          bio: profileRes.data.bio || "",
          experience_years: profileRes.data.experience_years || 0,
          address: profileRes.data.address || "",
          city: profileRes.data.city || "",
          state: profileRes.data.state || "",
          zip_code: profileRes.data.zip_code || "",
          specialties: profileRes.data.specialties || [],
          is_available: profileRes.data.is_available ?? true,
          profile_image: profileRes.data.profile_image || profileRes.data.profile_picture || "",
          instagram_handle: profileRes.data.instagram_handle || "",
          google_business_url: profileRes.data.google_business_url || ""
        });
        
        if (Array.isArray(profileRes.data.schedule) && profileRes.data.schedule.length > 0) {
          setSchedule(profileRes.data.schedule);
        }
        if (Array.isArray(profileRes.data.blocked_times) && profileRes.data.blocked_times.length > 0) {
          setBlockedTimes(profileRes.data.blocked_times);
        }
        if (profileRes.data.loyalty_config) {
          setLoyaltyConfig(profileRes.data.loyalty_config);
        }
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Profile image upload handler
  const handleProfileImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingProfileImage(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'profile');

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = response.data.url;
      setProfileForm(prev => ({ ...prev, profile_image: imageUrl }));
      
      // Auto-save profile with new image
      await axios.put(`${API}/stylists/profile`, {
        ...profileForm,
        profile_image: imageUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Profile photo updated!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploadingProfileImage(false);
      if (e.target) e.target.value = '';
    }
  };

  const saveProfile = async () => {
    try {
      await axios.put(`${API}/stylists/profile`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated successfully!");
      fetchData();
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const saveSchedule = async () => {
    try {
      await axios.post(`${API}/stylists/schedule`, schedule, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Schedule updated!");
    } catch (error) {
      console.error("Schedule save error:", error);
      toast.error("Failed to update schedule");
    }
  };

  const saveBlockedTimes = async () => {
    try {
      await axios.post(`${API}/stylists/blocked-times`, blockedTimes, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Blocked times updated!");
    } catch (error) {
      toast.error("Failed to update blocked times");
    }
  };

  const addBlockedTime = () => {
    if (newBlockedTime.date && newBlockedTime.start_time && newBlockedTime.end_time) {
      setBlockedTimes([...blockedTimes, { ...newBlockedTime, id: Date.now() }]);
      setNewBlockedTime({ date: "", start_time: "", end_time: "", reason: "" });
    }
  };

  const removeBlockedTime = (id) => {
    setBlockedTimes(blockedTimes.filter(bt => bt.id !== id));
  };

  const saveLoyaltyConfig = async () => {
    try {
      await axios.put(`${API}/stylists/profile`, { loyalty_config: loyaltyConfig }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Loyalty program updated!");
    } catch (error) {
      toast.error("Failed to update loyalty program");
    }
  };

  const addService = async () => {
    try {
      await axios.post(`${API}/stylists/services`, newService, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Service added!");
      setShowAddService(false);
      setNewService({ name: "", description: "", duration_minutes: 30, price: 0, category: "haircut" });
      fetchData();
    } catch (error) {
      toast.error("Failed to add service");
    }
  };

  const deleteService = async (serviceId) => {
    try {
      await axios.delete(`${API}/stylists/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Service deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete service");
    }
  };

  const updateService = async () => {
    if (!editingService) return;
    try {
      await axios.put(`${API}/stylists/services/${editingService.service_id}`, {
        name: editingService.name,
        description: editingService.description,
        price: editingService.price,
        duration_minutes: editingService.duration_minutes,
        category: editingService.category
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Service updated!");
      setShowEditService(false);
      setEditingService(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update service");
    }
  };

  const addSelectedTemplates = async () => {
    if (selectedTemplates.length === 0) {
      toast.error("Please select at least one service");
      return;
    }
    const templates = serviceTemplates[profileForm.professional_type] || [];
    for (const templateName of selectedTemplates) {
      const template = templates.find(t => t.name === templateName);
      if (template) {
        // Use custom price/duration if set, otherwise use template defaults
        const customized = customTemplates[templateName];
        const serviceToAdd = {
          ...template,
          price: customized?.price ?? template.price,
          duration_minutes: customized?.duration_minutes ?? template.duration_minutes
        };
        try {
          await axios.post(`${API}/stylists/services`, serviceToAdd, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          console.error("Failed to add template service:", error);
        }
      }
    }
    toast.success(`Added ${selectedTemplates.length} services!`);
    setShowTemplateSelector(false);
    setSelectedTemplates([]);
    setCustomTemplates({}); // Clear custom prices
    fetchData();
  };

  const toggleTemplateSelection = (serviceName) => {
    setSelectedTemplates(prev => 
      prev.includes(serviceName) 
        ? prev.filter(n => n !== serviceName)
        : [...prev, serviceName]
    );
  };

  const loadTemplateServices = async (type) => {
    const templates = serviceTemplates[type] || [];
    for (const template of templates) {
      try {
        await axios.post(`${API}/stylists/services`, template, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error("Failed to add template service:", error);
      }
    }
    toast.success(`Loaded ${templates.length} services!`);
    fetchData();
  };

  const shareToSocial = (platform, imageUrl) => {
    const text = encodeURIComponent(`Check out my latest work at MyHairChair! 💇✨`);
    const url = encodeURIComponent(window.location.origin);
    
    if (platform === "instagram") {
      // Instagram doesn't support direct sharing via URL, show instructions
      toast.info("Save the image and share it on Instagram with #TheSalonPortal");
    } else if (platform === "facebook") {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`, "_blank");
    } else if (platform === "twitter") {
      window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, "_blank");
    }
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      await axios.put(
        `${API}/appointments/${appointmentId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Appointment ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update appointment");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed": return "bg-emerald-100 text-emerald-800";
      case "pending": return "bg-amber-100 text-amber-800";
      case "completed": return "bg-stone-100 text-stone-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-stone-100 text-stone-800";
    }
  };

  const todayAppointments = appointments.filter(apt => {
    const today = new Date().toISOString().split('T')[0];
    return apt.date === today && apt.status !== 'cancelled';
  });

  const pendingAppointments = appointments.filter(apt => apt.status === 'pending');
  const completedToday = todayAppointments.filter(apt => apt.status === 'completed');
  const todayEarnings = completedToday.reduce((sum, apt) => sum + (apt.total_amount || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900">
        <div className="main-with-sidebar">
          <DashboardSkeleton dark={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900 pb-mobile-nav">
      {/* Mobile overlay */}
      <div 
        className={`mobile-overlay ${sidebarOpen ? 'mobile-overlay-visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      ></div>

      {/* Sidebar */}
      <aside className={`sidebar bg-stone-950 ${sidebarOpen ? 'sidebar-open' : ''} flex flex-col h-screen`}>
        <div className="p-6 pb-2">
          <Link to="/" className="flex items-center gap-2 mb-6 hover:opacity-80 transition-opacity cursor-pointer">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Scissors className="w-5 h-5 text-amber-500" />
            </div>
            <span className="font-display font-bold text-xl text-white">MyHairChair</span>
          </Link>
        </div>

        {/* Scrollable Navigation */}
        <nav className="flex-1 overflow-y-auto px-6 pb-4 space-y-1">
          {[
            { id: "dashboard", label: "Dashboard", icon: Calendar },
            { id: "inbox", label: "Inbox", icon: Inbox, highlight: true },
            { id: "clientmgmt", label: "My Clients", icon: Users, highlight: true },
            { id: "analytics", label: "Revenue", icon: BarChart3 },
            { id: "accounting", label: "Accounting", icon: Wallet, elite: true },
            { id: "reviews", label: "Reviews", icon: Star, highlight: true },
            { id: "clients", label: "Client Notes", icon: FileText },
            { id: "waitlist", label: "Waitlist", icon: Clock },
            { id: "marketing", label: "Marketing/CRM", icon: Users },
            { id: "rewards", label: "Rewards Engine", icon: Gift },
            { id: "referrals", label: "Referrals", icon: Share2 },
            { id: "invite", label: "Invite Clients", icon: UserPlus, highlight: true },
            { id: "reports", label: "Reports", icon: BarChart3, highlight: true },
            { id: "profile", label: "My Profile", icon: User },
            { id: "services", label: "Services & Pricing", icon: DollarSign },
            { id: "payments", label: "Payment Methods", icon: CreditCard },
            { id: "fees", label: "Fees & Earnings", icon: Wallet, highlight: true },
            { id: "schedule", label: "Schedule", icon: Clock },
            { id: "gallery", label: "Photo Gallery", icon: ImageIcon },
            { id: "widget", label: "Booking Widget", icon: Code },
            { id: "minisite", label: "Pro Mini-Site", icon: Globe, pro: true },
            { id: "display", label: "Display", icon: Palette },
            ...(user?.role === "owner" ? [
              { id: "booth", label: "Booth Rent", icon: Building2 },
              { id: "staff", label: "Staff Management", icon: UsersRound, elite: true },
              { id: "beta_analytics", label: "Beta Analytics", icon: Activity, beta: true }
            ] : []),
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                activeTab === item.id 
                  ? 'bg-amber-500/20 text-amber-500' 
                  : item.highlight
                    ? 'text-amber-400 hover:bg-amber-500/10'
                    : 'text-stone-400 hover:bg-white/5 hover:text-white'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
              {item.elite && (
                <Badge className="ml-auto bg-purple-500/20 text-purple-400 text-xs px-1.5">Elite</Badge>
              )}
              {item.beta && (
                <Badge className="ml-auto bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs px-1.5">BETA</Badge>
              )}
              {item.highlight && (
                <Badge className="ml-auto bg-amber-500 text-black text-xs px-1.5">NEW</Badge>
              )}
            </button>
          ))}

          {/* HairChair Soldiers Section with Dropdown */}
          <div className="mt-4 pt-4 border-t border-white/10">
            <SoldierNavDropdown user={user} onOpenPlaybook={() => setShowPlaybook(true)} />
          </div>
        </nav>

        {/* Fixed Bottom Section */}
        <div className="p-4 border-t border-white/10 bg-stone-950">
          {/* Getting Started Guide Link */}
          <Link 
            to="/onboarding-guide" 
            className="flex items-center gap-2 mb-3 text-stone-400 hover:text-amber-400 transition-colors text-sm"
            data-testid="onboarding-guide-link"
          >
            <Sparkles className="w-4 h-4" />
            <span>Getting Started Guide</span>
            <ChevronRight className="w-4 h-4 ml-auto" />
          </Link>
          
          {/* Membership Upgrade Banner - More Prominent for Free Users (hidden for Super Admin) */}
          {(user?.membership?.tier || "free") === "free" && user?.email !== "georgebroughton71@gmail.com" ? (
            <button 
              onClick={() => navigate('/membership?source=sidebar')}
              className="w-full mb-3 p-3 bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 border border-amber-500/40 rounded-lg hover:border-amber-400/60 hover:from-amber-500/30 hover:via-orange-500/30 hover:to-amber-500/30 transition-all group animate-pulse hover:animate-none"
              data-testid="sidebar-upgrade-btn"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Crown className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <span className="text-amber-400 text-sm font-bold">Level Up to Pro!</span>
                </div>
                <ArrowRight className="w-4 h-4 text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="text-stone-400 text-xs text-left">30 days FREE, then $29.99/mo • Unlimited clients & more</p>
            </button>
          ) : user?.email === "georgebroughton71@gmail.com" ? (
            <div className="mb-3 p-2.5 bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 text-sm font-medium">Super Admin</span>
                <Badge className="ml-auto bg-purple-500 text-white text-xs">Full Access</Badge>
              </div>
            </div>
          ) : (
            <Link 
              to="/membership" 
              className="block mb-3 p-2.5 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 rounded-lg hover:border-amber-400/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-medium">
                  {user?.membership?.tier === "elite" ? "Elite Member" : "Pro Member"}
                </span>
                <Badge className="ml-auto bg-amber-500 text-black text-xs">Active</Badge>
              </div>
            </Link>
          )}
          
          {/* User Guide Link in Sidebar */}
          <Link 
            to="/user-guide?role=stylist"
            className="w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-lg text-stone-400 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="user-guide-sidebar-link"
          >
            <BookOpen className="w-4 h-4" />
            <span className="text-sm">User Guide</span>
          </Link>
          
          {/* Owner Dashboard Link - For Salon Owners and Elite Members */}
          {(user?.role === "owner" || 
            user?.email === "georgebroughton71@gmail.com" || 
            user?.membership?.tier === "elite" ||
            user?.membershipTier === "elite") && (
            <Link 
              to="/owner-dashboard"
              className="w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-500/30 text-purple-300 hover:border-purple-400/50 hover:text-white transition-colors"
              data-testid="owner-dashboard-link"
            >
              <Building2 className="w-4 h-4" />
              <span className="text-sm font-medium">Salon Owner View</span>
              <Crown className="w-3 h-3 text-amber-400 ml-1" />
              <ChevronRight className="w-4 h-4 ml-auto" />
            </Link>
          )}
          
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-white text-sm truncate">{user?.name}</div>
              <div className="text-xs text-stone-500 capitalize">{profileForm.professional_type || "Stylist"}</div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-stone-400 hover:text-white hover:bg-white/5 h-9"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Top Upgrade Banner for Free Users (not shown for Super Admin) */}
      {(user?.membership?.tier || "free") === "free" && (
        <UpgradeBanner userTier="free" userEmail={user?.email || ""} />
      )}

      {/* Main Content */}
      <main className="main-with-sidebar" data-testid="stylist-dashboard">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-stone-900/80 backdrop-blur-xl border-b border-white/10">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <button 
                className="lg:hidden p-2 text-white flex-shrink-0"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              {/* Home Button - Icon only on mobile */}
              <Link 
                to="/" 
                className="flex items-center gap-2 p-2 sm:px-3 sm:py-2 bg-stone-800 hover:bg-stone-700 rounded-lg transition-colors group flex-shrink-0"
                data-testid="stylist-home-btn"
              >
                <Home className="w-4 h-4 text-gray-400 group-hover:text-white" />
                <span className="text-sm text-gray-400 group-hover:text-white hidden sm:inline">Home</span>
              </Link>
              {/* Page title - hidden on mobile for dashboard, truncated on other pages */}
              <h1 className="font-display text-lg sm:text-2xl font-bold text-white hidden sm:block truncate">
                {activeTab === "dashboard" ? `Welcome, ${user?.name?.split(' ')[0]}!` : 
                 activeTab === "profile" ? "My Profile" :
                 activeTab === "services" ? "Services & Pricing" :
                 activeTab === "payments" ? "Payment Methods" :
                 activeTab === "fees" ? "Fees & Earnings" :
                 activeTab === "schedule" ? "Schedule Settings" :
                 activeTab === "rewards" ? "Rewards Command Center" :
                 activeTab === "analytics" ? "Revenue & Analytics" :
                 activeTab === "accounting" ? "Accounting & Finances" :
                 activeTab === "booth" ? "Booth Management" :
                 activeTab === "staff" ? "Staff Management" :
                 activeTab === "widget" ? "Booking Widget" :
                 activeTab === "minisite" ? "Pro Mini-Site" :
                 activeTab === "clients" ? "Client Notes" :
                 activeTab === "clientmgmt" ? "My Clients" :
                 activeTab === "waitlist" ? "Waitlist Queue" :
                 activeTab === "marketing" ? "Marketing & CRM" :
                 activeTab === "gallery" ? "Photo Gallery" :
                 activeTab === "referrals" ? "Referral Dashboard" :
                 activeTab === "reviews" ? "Client Reviews" :
                 activeTab === "invite" ? "Invite Clients" :
                 activeTab === "reports" ? "Reports & Analytics" :
                 activeTab === "inbox" ? "Inbox & Notifications" :
                 activeTab === "display" ? "Display Settings" :
                 activeTab === "beta_analytics" ? "Beta Analytics Dashboard" :
                 "Portfolio"}
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* User Guide Icon - Header */}
              <Link 
                to="/user-guide?role=stylist"
                className="p-2 rounded-lg bg-stone-800 hover:bg-stone-700 transition-colors"
                title="User Guide"
                data-testid="user-guide-header-link"
              >
                <BookOpen className="w-5 h-5 text-stone-400 hover:text-white" />
              </Link>
              {/* Availability badge - smaller on mobile */}
              <Badge className={`${profileForm.is_available ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'} text-xs sm:text-sm`}>
                <div className={`w-2 h-2 ${profileForm.is_available ? 'bg-emerald-500' : 'bg-red-500'} rounded-full mr-1 sm:mr-2`}></div>
                <span className="hidden sm:inline">{profileForm.is_available ? "Available" : "Unavailable"}</span>
                <span className="sm:hidden">{profileForm.is_available ? "On" : "Off"}</span>
              </Badge>
              <ProfileDropdown user={user} />
            </div>
          </div>
        </header>

        <div className="p-6">
          {/* Dashboard Tab */}
          {activeTab === "dashboard" && (
            <div className="space-y-6">
              {/* Onboarding Checklist - Non-intrusive */}
              <OnboardingChecklist 
                userRole="stylist" 
                userData={{ 
                  name: user?.name, 
                  email: user?.email, 
                  services: profile?.services,
                  gallery: profile?.gallery
                }}
              />

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="bg-stone-800/50 border-stone-700" data-testid="today-appointments-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-stone-400 mb-1">Today's Appointments</p>
                        <p className="text-3xl font-display font-bold text-white">
                          {todayAppointments.length}
                        </p>
                      </div>
                      <Calendar className="w-10 h-10 text-amber-500/30" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-stone-800/50 border-stone-700" data-testid="pending-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-stone-400 mb-1">Pending Requests</p>
                        <p className="text-3xl font-display font-bold text-amber-500">
                          {pendingAppointments.length}
                        </p>
                      </div>
                      <Clock className="w-10 h-10 text-amber-500/30" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-stone-800/50 border-stone-700" data-testid="completed-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-stone-400 mb-1">Completed Today</p>
                        <p className="text-3xl font-display font-bold text-white">
                          {completedToday.length}
                        </p>
                      </div>
                      <CheckCircle className="w-10 h-10 text-emerald-500/30" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30" data-testid="earnings-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-amber-400/80 mb-1">Today's Earnings</p>
                        <p className="text-3xl font-display font-bold text-white">
                          ${todayEarnings.toFixed(2)}
                        </p>
                      </div>
                      <DollarSign className="w-10 h-10 text-amber-500/50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Getting Started Checklist */}
              {showGettingStarted && (
                <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                          <h3 className="font-display text-lg font-semibold text-white">Getting Started</h3>
                          <p className="text-sm text-stone-400">Complete these steps to start receiving bookings</p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-stone-400 hover:text-white"
                        onClick={() => setShowGettingStarted(false)}
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      {[
                        {
                          label: "Set your professional type",
                          done: !!profileForm.professional_type,
                          action: () => setActiveTab("profile"),
                          hint: "Barber, Stylist, etc."
                        },
                        {
                          label: "Add your services & prices",
                          done: (profile?.services?.length || 0) > 0,
                          action: () => setActiveTab("services"),
                          hint: "What you offer"
                        },
                        {
                          label: "Set your working hours",
                          done: schedule.some(d => d.is_working),
                          action: () => setActiveTab("schedule"),
                          hint: "When you're available"
                        },
                        {
                          label: "Complete your profile",
                          done: !!profileForm.bio && !!profileForm.city,
                          action: () => setActiveTab("profile"),
                          hint: "Bio, location, specialties"
                        }
                      ].map((item, idx) => {
                        const completedCount = [
                          !!profileForm.professional_type,
                          (profile?.services?.length || 0) > 0,
                          schedule.some(d => d.is_working),
                          !!profileForm.bio && !!profileForm.city
                        ].filter(Boolean).length;
                        
                        return (
                          <div 
                            key={idx}
                            onClick={item.action}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                              item.done 
                                ? 'bg-emerald-500/10 border border-emerald-500/30' 
                                : 'bg-stone-800/50 border border-stone-700 hover:border-amber-500/50'
                            }`}
                          >
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                              item.done ? 'bg-emerald-500' : 'border-2 border-stone-600'
                            }`}>
                              {item.done && <Check className="w-4 h-4 text-white" />}
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium ${item.done ? 'text-emerald-400' : 'text-white'}`}>
                                {item.label}
                              </p>
                              <p className="text-xs text-stone-500">{item.hint}</p>
                            </div>
                            {!item.done && (
                              <ChevronRight className="w-4 h-4 text-amber-400" />
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Progress Bar */}
                    {(() => {
                      const completedCount = [
                        !!profileForm.professional_type,
                        (profile?.services?.length || 0) > 0,
                        schedule.some(d => d.is_working),
                        !!profileForm.bio && !!profileForm.city
                      ].filter(Boolean).length;
                      const progress = (completedCount / 4) * 100;
                      
                      return (
                        <div className="mt-4 pt-4 border-t border-stone-700">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-stone-400">{completedCount}/4 completed</span>
                            {completedCount === 4 && (
                              <span className="text-sm text-emerald-400 font-medium">🎉 All done!</span>
                            )}
                          </div>
                          <div className="h-2 bg-stone-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 rounded-full transition-all duration-500"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              )}

              {/* Pending Appointments */}
              {pendingAppointments.length > 0 && (
                <Card className="bg-stone-800/50 border-amber-500/30">
                  <CardHeader>
                    <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      Pending Requests
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {pendingAppointments.map((apt) => (
                        <div 
                          key={apt.appointment_id}
                          className="flex items-center justify-between p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl"
                          data-testid={`pending-apt-${apt.appointment_id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                              <User className="w-6 h-6 text-amber-400" />
                            </div>
                            <div>
                              <p className="font-medium text-white">{apt.client?.name}</p>
                              <p className="text-sm text-stone-400">
                                {apt.service?.name} • {apt.date} at {apt.time}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-amber-400 mr-4">
                              ${apt.total_amount?.toFixed(2)}
                            </span>
                            <Button 
                              size="sm" 
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => updateAppointmentStatus(apt.appointment_id, 'confirmed')}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Accept
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                              onClick={() => updateAppointmentStatus(apt.appointment_id, 'cancelled')}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Decline
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Receipt Verification Section */}
              <Card className="bg-stone-800/50 border-stone-700">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-amber-500" />
                    Payment Receipts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ReceiptVerification />
                </CardContent>
              </Card>

              {/* Today's Schedule */}
              <Card className="bg-stone-800/50 border-stone-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-display text-xl text-white">Today's Schedule</CardTitle>
                  {todayAppointments.length > 0 && (
                    <div className="flex items-center gap-2">
                      {(user?.membership_tier === 'pro' || user?.membership_tier === 'elite') ? (
                        <a 
                          href={`${API}/appointments/calendar/export-all`}
                          download="myhairchair_appointments.ics"
                          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-stone-700 hover:bg-stone-600 text-amber-400 rounded-lg transition-colors"
                          onClick={(e) => {
                            e.preventDefault();
                            fetch(`${API}/appointments/calendar/export-all`, {
                              headers: { Authorization: `Bearer ${token}` }
                            })
                            .then(res => {
                              if (res.status === 403) {
                                toast.error("Upgrade to Pro to export appointments to your calendar");
                                return null;
                              }
                              return res.blob();
                            })
                            .then(blob => {
                              if (blob) {
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = 'myhairchair_appointments.ics';
                                a.click();
                                window.URL.revokeObjectURL(url);
                              }
                            })
                            .catch(err => console.error('Download failed:', err));
                          }}
                        >
                          <Download className="w-4 h-4" />
                          Export All
                        </a>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
                          onClick={() => navigate('/membership')}
                        >
                          <Crown className="w-4 h-4 mr-1" />
                          Export All (Pro)
                        </Button>
                      )}
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {todayAppointments.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                      <p className="text-stone-500">No appointments scheduled for today</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {todayAppointments.map((apt) => (
                        <div 
                          key={apt.appointment_id}
                          className="p-4 bg-stone-700/50 rounded-xl hover:bg-stone-700 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="text-center min-w-[60px] px-3 py-2 bg-stone-600 rounded-lg">
                                <p className="text-lg font-bold text-white">{apt.time}</p>
                              </div>
                              <div>
                                <p className="font-medium text-white">{apt.client?.name}</p>
                                <p className="text-sm text-stone-400">{apt.service?.name}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {/* Add to Calendar Button - Pro Feature */}
                              {(user?.membership_tier === 'pro' || user?.membership_tier === 'elite') ? (
                                <a 
                                  href={`${API}/appointments/${apt.appointment_id}/calendar`}
                                  download={`appointment_${apt.appointment_id}.ics`}
                                  className="p-2 text-stone-400 hover:text-amber-400 hover:bg-stone-600 rounded-lg transition-colors"
                                  title="Add to Calendar"
                                >
                                  <Download className="w-4 h-4" />
                                </a>
                              ) : (
                                <button
                                  onClick={() => {
                                    toast("Upgrade to Pro to export appointments to your calendar", {
                                      action: {
                                        label: "Upgrade",
                                        onClick: () => navigate('/membership')
                                      }
                                    });
                                  }}
                                  className="p-2 text-stone-500 hover:text-amber-400 hover:bg-stone-600 rounded-lg transition-colors relative"
                                  title="Add to Calendar (Pro Feature)"
                                >
                                  <Download className="w-4 h-4" />
                                  <Crown className="w-2.5 h-2.5 absolute -top-0.5 -right-0.5 text-amber-400" />
                                </button>
                              )}
                              <Badge className={getStatusColor(apt.status)}>
                                {apt.status}
                              </Badge>
                              <span className="font-medium text-white">
                                ${apt.total_amount?.toFixed(2)}
                              </span>
                              {apt.status === 'confirmed' && (
                                <Button 
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => updateAppointmentStatus(apt.appointment_id, 'completed')}
                                >
                                  Complete
                                </Button>
                              )}
                              {apt.status === 'completed' && !apt.refund_id && (
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                  onClick={() => {
                                    setSelectedAppointmentForRefund(apt);
                                    setShowRefundModal(true);
                                  }}
                                  data-testid={`refund-btn-${apt.appointment_id}`}
                                >
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Refund
                                </Button>
                              )}
                              {apt.refund_id && (
                                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                                  Refunded
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          {/* Payment Status Row */}
                          {apt.payment_method && apt.payment_method !== 'stripe' && apt.payment_method !== 'demo' && (
                            <div className="mt-3 pt-3 border-t border-stone-600 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {apt.payment_status === 'paid' ? (
                                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Paid
                                  </Badge>
                                ) : apt.receipt_status === 'pending_verification' ? (
                                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 animate-pulse">
                                    <Receipt className="w-3 h-3 mr-1" />
                                    Receipt Pending
                                  </Badge>
                                ) : (
                                  <Badge className="bg-stone-500/20 text-stone-400 border-stone-500/30">
                                    <DollarSign className="w-3 h-3 mr-1" />
                                    Pay at Service
                                  </Badge>
                                )}
                                <span className="text-xs text-stone-500 capitalize">
                                  via {apt.payment_method === 'pay_at_salon' ? 'Pay at Service' : apt.payment_method}
                                </span>
                              </div>
                              
                              {apt.receipt_status === 'pending_verification' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                                  onClick={() => setActiveTab('payments')}
                                >
                                  <Receipt className="w-3 h-3 mr-1" />
                                  Verify Receipt
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === "profile" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Profile Form */}
              <div className="space-y-6">
              {/* Profile Photo Card */}
              <Card className="bg-stone-800/50 border-stone-700">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                    <Camera className="w-5 h-5 text-amber-500" />
                    Profile Photo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6">
                    {/* Profile Image Preview */}
                    <div className="relative">
                      <div className="w-28 h-28 rounded-full overflow-hidden bg-stone-700 border-4 border-amber-500/30">
                        {profileForm.profile_image ? (
                          <img 
                            src={profileForm.profile_image} 
                            alt="Profile" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-12 h-12 text-stone-500" />
                          </div>
                        )}
                      </div>
                      {uploadingProfileImage && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    
                    {/* Upload Controls */}
                    <div className="flex-1">
                      <input
                        type="file"
                        ref={profileImageInputRef}
                        onChange={handleProfileImageUpload}
                        accept="image/*"
                        className="hidden"
                      />
                      <Button
                        onClick={() => profileImageInputRef.current?.click()}
                        disabled={uploadingProfileImage}
                        className="bg-amber-500 hover:bg-amber-600 text-black"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {profileForm.profile_image ? "Change Photo" : "Upload Photo"}
                      </Button>
                      <p className="text-stone-400 text-sm mt-2">
                        Recommended: Square image, at least 400x400px
                      </p>
                      <p className="text-stone-500 text-xs mt-1">
                        Max file size: 5MB • JPG, PNG, or WebP
                      </p>
                    </div>
                  </div>
                  
                  {/* Gallery Note */}
                  <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-amber-400 text-sm flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      <span>
                        <strong>Want to showcase your work?</strong> Head over to the{" "}
                        <button 
                          onClick={() => setActiveTab("gallery")}
                          className="underline hover:text-amber-300"
                        >
                          Photo Gallery
                        </button>{" "}
                        tab to create a full portfolio of your best styles!
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-stone-800/50 border-stone-700">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-white">Professional Type</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {professionalTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setProfileForm({ ...profileForm, professional_type: type.id })}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          profileForm.professional_type === type.id
                            ? 'border-amber-500 bg-amber-500/10'
                            : 'border-stone-600 hover:border-stone-500'
                        }`}
                        data-testid={`type-${type.id}`}
                      >
                        <div className="font-medium text-white">{type.name}</div>
                        <div className="text-xs text-stone-400 mt-1">{type.description}</div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-stone-800/50 border-stone-700">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-white">About You</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-stone-300">Bio</Label>
                    <Textarea
                      value={profileForm.bio}
                      onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                      placeholder="Tell clients about yourself, your style, and specialties..."
                      className="mt-2 bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                      rows={4}
                      data-testid="bio-input"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-300">Years of Experience</Label>
                    <Input
                      type="number"
                      min="0"
                      max="50"
                      value={profileForm.experience_years === 0 ? '' : profileForm.experience_years}
                      onChange={(e) => {
                        const val = e.target.value;
                        // Allow empty string for typing, convert to number on actual input
                        if (val === '') {
                          setProfileForm({ ...profileForm, experience_years: 0 });
                        } else {
                          // Remove leading zeros by parsing and re-converting
                          const numVal = parseInt(val, 10);
                          setProfileForm({ ...profileForm, experience_years: isNaN(numVal) ? 0 : numVal });
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure we have a valid number on blur
                        const numVal = parseInt(e.target.value, 10);
                        setProfileForm({ ...profileForm, experience_years: isNaN(numVal) ? 0 : numVal });
                      }}
                      placeholder="0"
                      className="mt-2 bg-stone-700 border-stone-600 text-white"
                      data-testid="experience-input"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-300 flex items-center gap-2">
                      <svg className="w-4 h-4 text-pink-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                      Instagram Handle
                    </Label>
                    <div className="relative mt-2">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-pink-400">@</span>
                      <Input
                        value={profileForm.instagram_handle}
                        onChange={(e) => setProfileForm({ ...profileForm, instagram_handle: e.target.value.replace('@', '') })}
                        placeholder="yourhandle"
                        className="pl-8 bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                        data-testid="instagram-handle-input"
                      />
                    </div>
                    <p className="text-xs text-stone-500 mt-1">Your IG handle shows on your Social Cards</p>
                  </div>
                  
                  {/* Google Business Profile */}
                  <div className="pt-4 border-t border-stone-700">
                    <Label className="text-stone-300 flex items-center gap-2">
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Google Business Profile
                    </Label>
                    <Input
                      value={profileForm.google_business_url}
                      onChange={(e) => setProfileForm({ ...profileForm, google_business_url: e.target.value })}
                      placeholder="https://g.page/your-business or Google Maps link"
                      className="mt-2 bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                      data-testid="google-business-url-input"
                    />
                    <div className="mt-2 p-3 bg-stone-900/50 rounded-lg border border-stone-700">
                      <p className="text-xs text-amber-400 font-medium mb-2">📍 How to find your Google Business link:</p>
                      <ol className="text-xs text-stone-400 space-y-1 list-decimal list-inside">
                        <li>Search your business name on Google Maps</li>
                        <li>Click on your business listing</li>
                        <li>Click &quot;Share&quot; → &quot;Copy link&quot;</li>
                        <li>Paste the link here</li>
                      </ol>
                      <p className="text-xs text-stone-500 mt-2">
                        Don&apos;t have a Google Business? <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Create one free →</a>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-stone-300">Available for Bookings</Label>
                      <p className="text-xs text-stone-500">Turn off when you&apos;re on vacation</p>
                    </div>
                    <Switch
                      checked={profileForm.is_available}
                      onCheckedChange={(checked) => setProfileForm({ ...profileForm, is_available: checked })}
                      data-testid="availability-switch"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-stone-800/50 border-stone-700">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-amber-500" />
                    Location
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-stone-300">Address</Label>
                    <Input
                      value={profileForm.address}
                      onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                      placeholder="Street address"
                      className="mt-2 bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                      data-testid="address-input"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-stone-300">City</Label>
                      <Input
                        value={profileForm.city}
                        onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                        className="mt-2 bg-stone-700 border-stone-600 text-white"
                        data-testid="city-input"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-300">State</Label>
                      <Input
                        value={profileForm.state}
                        onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
                        className="mt-2 bg-stone-700 border-stone-600 text-white"
                        data-testid="state-input"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-300">ZIP</Label>
                      <Input
                        value={profileForm.zip_code}
                        onChange={(e) => setProfileForm({ ...profileForm, zip_code: e.target.value })}
                        className="mt-2 bg-stone-700 border-stone-600 text-white"
                        data-testid="zip-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Methods */}
              <PaymentMethodsCard onUpgradeClick={() => navigate('/membership')} />

              {/* Social Media Sharing */}
              <SocialShareCard onUpgradeClick={() => navigate('/membership')} />

              <Button 
                onClick={saveProfile}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium h-12"
                data-testid="save-profile-btn"
              >
                Save Profile
              </Button>
              </div>

              {/* Right Column - Client Preview */}
              <div className="space-y-6">
                <Card className="bg-stone-800/50 border-stone-700 sticky top-4">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-display text-lg text-white flex items-center gap-2">
                        <Eye className="w-5 h-5 text-amber-500" />
                        Client Preview
                      </CardTitle>
                      <span className="text-xs text-stone-500 bg-stone-700 px-2 py-1 rounded">Live Preview</span>
                    </div>
                    <p className="text-xs text-stone-400">This is how clients see your profile when booking</p>
                  </CardHeader>
                  <CardContent>
                    {/* Preview Card - Simulating Client View */}
                    <div className="bg-gradient-to-b from-stone-900 to-stone-800 rounded-xl p-5 border border-stone-600 shadow-xl">
                      {/* Profile Header */}
                      <div className="flex items-center gap-4 pb-4 border-b border-stone-700">
                        <div className="w-20 h-20 rounded-full overflow-hidden bg-stone-700 border-2 border-amber-500/50 flex-shrink-0">
                          {profileForm.profile_image ? (
                            <img 
                              src={profileForm.profile_image} 
                              alt="Profile" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="w-8 h-8 text-stone-500" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-display text-lg text-white truncate">{user?.name || "Your Name"}</h3>
                          <p className="text-amber-400 text-sm font-medium">
                            {profileForm.professional_type ? 
                              profileForm.professional_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                              : "Professional Type"}
                          </p>
                          {profileForm.city && profileForm.state && (
                            <p className="text-stone-400 text-xs flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {profileForm.city}, {profileForm.state}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Bio Section */}
                      <div className="pt-4 pb-3">
                        <p className="text-stone-300 text-sm line-clamp-3">
                          {profileForm.bio || "Your bio will appear here. Tell clients about your experience and specialties!"}
                        </p>
                      </div>
                      
                      {/* Specialties */}
                      {profileForm.specialties?.length > 0 && (
                        <div className="flex flex-wrap gap-1 pb-3">
                          {profileForm.specialties.slice(0, 4).map((specialty, idx) => (
                            <span key={idx} className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">
                              {specialty}
                            </span>
                          ))}
                          {profileForm.specialties.length > 4 && (
                            <span className="text-xs text-stone-500">+{profileForm.specialties.length - 4} more</span>
                          )}
                        </div>
                      )}
                      
                      {/* Services Preview */}
                      <div className="pt-3 border-t border-stone-700">
                        <p className="text-xs text-stone-500 mb-2">Popular Services</p>
                        {profile?.services?.length > 0 ? (
                          <div className="space-y-2">
                            {profile.services.slice(0, 3).map((service, idx) => (
                              <div key={idx} className="flex justify-between items-center text-sm">
                                <span className="text-stone-300">{service.name}</span>
                                <span className="text-amber-400 font-medium">${service.price?.toFixed(2) || '0.00'}</span>
                              </div>
                            ))}
                            {profile.services.length > 3 && (
                              <p className="text-xs text-stone-500">+{profile.services.length - 3} more services</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-stone-500 italic">Add services to show pricing</p>
                        )}
                      </div>
                      
                      {/* Availability Badge */}
                      <div className="pt-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${profileForm.is_available ? 'bg-green-400' : 'bg-red-400'}`} />
                          <span className="text-xs text-stone-400">
                            {profileForm.is_available ? 'Available for Bookings' : 'Currently Unavailable'}
                          </span>
                        </div>
                        {profileForm.instagram_handle && (
                          <a 
                            href={`https://instagram.com/${profileForm.instagram_handle}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-pink-400 hover:text-pink-300 transition-colors"
                          >
                            <Instagram className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                      
                      {/* Book Now Button Preview */}
                      <Button 
                        disabled
                        className="w-full mt-4 bg-amber-500 text-black font-medium cursor-default"
                      >
                        Book Appointment
                      </Button>
                    </div>
                    
                    <p className="text-xs text-stone-500 text-center mt-3">
                      Updates automatically as you edit your profile
                    </p>
                    
                    {/* Open Full Preview Button */}
                    {user?.user_id && (
                      <Link 
                        to={`/p/${user.profile_slug || user.user_id}?preview=true`}
                        className="block mt-4"
                      >
                        <Button 
                          variant="outline" 
                          className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Open Full Preview
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Services Tab */}
          {activeTab === "services" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h2 className="font-display text-xl text-white">Your Services</h2>
                  <p className="text-sm text-stone-400">Set your prices and service offerings</p>
                </div>
                <div className="flex gap-3 flex-wrap">
                  {profileForm.professional_type && (
                    <Dialog open={showTemplateSelector} onOpenChange={setShowTemplateSelector}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                          data-testid="load-templates-btn"
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          Browse Default Services
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-stone-800 border-stone-700 max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-white">Select Services to Add</DialogTitle>
                          <p className="text-sm text-stone-400">Select services and customize your prices before adding.</p>
                        </DialogHeader>
                        <div className="space-y-3 pt-4">
                          {(serviceTemplates[profileForm.professional_type] || []).map((template, idx) => {
                            const isSelected = selectedTemplates.includes(template.name);
                            const alreadyHas = profile?.services?.some(s => s.name === template.name);
                            const customPrice = customTemplates[template.name]?.price ?? template.price;
                            const customDuration = customTemplates[template.name]?.duration_minutes ?? template.duration_minutes;
                            
                            return (
                              <div 
                                key={idx}
                                className={`p-4 rounded-lg border transition-all ${
                                  alreadyHas 
                                    ? 'bg-stone-700/30 border-stone-600 opacity-50'
                                    : isSelected 
                                      ? 'bg-amber-500/20 border-amber-500' 
                                      : 'bg-stone-700/50 border-stone-600 hover:border-stone-500'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div 
                                    className={`flex items-center gap-3 flex-1 ${!alreadyHas ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                    onClick={() => !alreadyHas && toggleTemplateSelection(template.name)}
                                  >
                                    <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-1 ${
                                      alreadyHas ? 'bg-stone-600 border-stone-500' :
                                      isSelected ? 'bg-amber-500 border-amber-500' : 'border-stone-500'
                                    }`}>
                                      {(isSelected || alreadyHas) && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                    <div>
                                      <p className="font-medium text-white">{template.name}</p>
                                      <p className="text-xs text-stone-400">{template.description}</p>
                                      {alreadyHas && <p className="text-xs text-stone-500 mt-1">Already added</p>}
                                    </div>
                                  </div>
                                  
                                  {/* Editable Price and Duration */}
                                  {!alreadyHas && (
                                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                      <div className="w-24">
                                        <label className="text-[10px] text-stone-500 uppercase tracking-wide">Price</label>
                                        <div className="relative">
                                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-400 text-sm">$</span>
                                          <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={customPrice}
                                            onChange={(e) => setCustomTemplates(prev => ({
                                              ...prev,
                                              [template.name]: {
                                                ...prev[template.name],
                                                price: parseFloat(e.target.value) || 0
                                              }
                                            }))}
                                            className="w-full pl-6 pr-2 py-1.5 text-sm bg-stone-700 border border-stone-600 rounded text-amber-400 font-bold focus:border-amber-500 focus:outline-none"
                                          />
                                        </div>
                                      </div>
                                      <div className="w-20">
                                        <label className="text-[10px] text-stone-500 uppercase tracking-wide">Duration</label>
                                        <div className="relative">
                                          <input
                                            type="number"
                                            step="5"
                                            min="5"
                                            value={customDuration}
                                            onChange={(e) => setCustomTemplates(prev => ({
                                              ...prev,
                                              [template.name]: {
                                                ...prev[template.name],
                                                duration_minutes: parseInt(e.target.value) || 15
                                              }
                                            }))}
                                            className="w-full pl-2 pr-8 py-1.5 text-sm bg-stone-700 border border-stone-600 rounded text-stone-300 focus:border-amber-500 focus:outline-none"
                                          />
                                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 text-xs">min</span>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex justify-between items-center pt-4 border-t border-stone-700 mt-4">
                          <p className="text-sm text-stone-400">{selectedTemplates.length} selected</p>
                          <Button 
                            onClick={addSelectedTemplates}
                            className="bg-amber-500 hover:bg-amber-600 text-black"
                            disabled={selectedTemplates.length === 0}
                          >
                            Add Selected Services
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                  <Dialog open={showAddService} onOpenChange={setShowAddService}>
                    <DialogTrigger asChild>
                      <Button className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="add-service-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Custom Service
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-stone-800 border-stone-700">
                      <DialogHeader>
                        <DialogTitle className="text-white">Add New Service</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <Label className="text-stone-300">Service Name</Label>
                          <Input
                            value={newService.name}
                            onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                            placeholder="e.g., Men's Haircut"
                            className="mt-2 bg-stone-700 border-stone-600 text-white"
                            data-testid="new-service-name"
                          />
                        </div>
                        <div>
                          <Label className="text-stone-300">Description</Label>
                          <Textarea
                            value={newService.description}
                            onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                            placeholder="Describe the service..."
                            className="mt-2 bg-stone-700 border-stone-600 text-white"
                            data-testid="new-service-desc"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-stone-300">Price ($)</Label>
                            <Input
                              type="number"
                              value={newService.price}
                              onChange={(e) => setNewService({ ...newService, price: parseFloat(e.target.value) || 0 })}
                              className="mt-2 bg-stone-700 border-stone-600 text-white"
                              data-testid="new-service-price"
                            />
                          </div>
                          <div>
                            <Label className="text-stone-300">Duration (min)</Label>
                            <Input
                              type="number"
                              value={newService.duration_minutes}
                              onChange={(e) => setNewService({ ...newService, duration_minutes: parseInt(e.target.value) || 30 })}
                              className="mt-2 bg-stone-700 border-stone-600 text-white"
                              data-testid="new-service-duration"
                            />
                          </div>
                        </div>
                        <Button 
                          onClick={addService}
                          className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                          data-testid="confirm-add-service"
                        >
                          Add Service
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              <div className="grid gap-4">
                {profile?.services?.length === 0 ? (
                  <Card className="bg-stone-800/50 border-stone-700">
                    <CardContent className="py-12 text-center">
                      <DollarSign className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                      <p className="text-stone-400 mb-4">No services added yet</p>
                      <p className="text-sm text-stone-500">
                        {profileForm.professional_type 
                          ? "Click 'Browse Default Services' to get started quickly!"
                          : "Select your professional type first, then add your services."}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  profile?.services?.map((service) => (
                    <Card key={service.service_id} className="bg-stone-800/50 border-stone-700 hover:border-stone-600 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                              <Scissors className="w-6 h-6 text-amber-400" />
                            </div>
                            <div>
                              <h3 className="font-medium text-white">{service.name}</h3>
                              <p className="text-sm text-stone-400">{service.description}</p>
                              <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                                <span>{service.duration_minutes} min</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xl font-bold text-amber-400">${service.price?.toFixed(2)}</span>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-stone-400 hover:text-amber-400"
                              onClick={() => {
                                setEditingService({...service});
                                setShowEditService(true);
                              }}
                            >
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-stone-400 hover:text-red-400"
                              onClick={() => deleteService(service.service_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Edit Service Dialog */}
              <Dialog open={showEditService} onOpenChange={setShowEditService}>
                <DialogContent className="bg-stone-800 border-stone-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">Edit Service</DialogTitle>
                  </DialogHeader>
                  {editingService && (
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label className="text-stone-300">Service Name</Label>
                        <Input
                          value={editingService.name}
                          onChange={(e) => setEditingService({ ...editingService, name: e.target.value })}
                          className="mt-2 bg-stone-700 border-stone-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-stone-300">Description</Label>
                        <Textarea
                          value={editingService.description}
                          onChange={(e) => setEditingService({ ...editingService, description: e.target.value })}
                          className="mt-2 bg-stone-700 border-stone-600 text-white"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-stone-300">Price ($)</Label>
                          <Input
                            type="number"
                            value={editingService.price}
                            onChange={(e) => setEditingService({ ...editingService, price: parseFloat(e.target.value) || 0 })}
                            className="mt-2 bg-stone-700 border-stone-600 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-stone-300">Duration (min)</Label>
                          <Input
                            type="number"
                            value={editingService.duration_minutes}
                            onChange={(e) => setEditingService({ ...editingService, duration_minutes: parseInt(e.target.value) || 30 })}
                            className="mt-2 bg-stone-700 border-stone-600 text-white"
                          />
                        </div>
                      </div>
                      <Button 
                        onClick={updateService}
                        className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                      >
                        Save Changes
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* Smart Upsell / Add-On Services Section - Premium Feature */}
              <Card className="bg-gradient-to-br from-emerald-900/30 to-green-900/20 border-emerald-500/30 mt-8">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="font-display text-xl text-white">Smart Upsells</CardTitle>
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">
                            NEW
                          </Badge>
                        </div>
                        <p className="text-sm text-emerald-300/70">Boost revenue by suggesting add-on services during booking</p>
                      </div>
                    </div>
                    {(user?.membership?.tier === "pro" || user?.membership?.tier === "elite" || user?.is_super_admin) ? (
                      <Button 
                        onClick={() => setShowUpsellsConfig(true)}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white"
                        data-testid="configure-upsells-btn"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Configure Upsells
                      </Button>
                    ) : (
                      <Link to="/membership?feature=smart_upsell" data-testid="upgrade-for-upsells-btn">
                        <Button className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black">
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade to Pro
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-black/30 rounded-lg p-4 border border-emerald-500/20">
                      <div className="text-2xl font-bold text-emerald-400">+25%</div>
                      <div className="text-sm text-stone-400">Average revenue increase</div>
                    </div>
                    <div className="bg-black/30 rounded-lg p-4 border border-emerald-500/20">
                      <div className="text-2xl font-bold text-emerald-400">Auto</div>
                      <div className="text-sm text-stone-400">Suggests based on service</div>
                    </div>
                    <div className="bg-black/30 rounded-lg p-4 border border-emerald-500/20">
                      <div className="text-2xl font-bold text-emerald-400">Bundle</div>
                      <div className="text-sm text-stone-400">Discounted package pricing</div>
                    </div>
                  </div>
                  {(user?.membership?.tier !== "pro" && user?.membership?.tier !== "elite" && !user?.is_super_admin) && (
                    <p className="text-center text-emerald-300/50 text-sm mt-4">
                      Upgrade to Pro to start suggesting add-on services and increase your average ticket value
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Upsells Configuration Dialog */}
              <Dialog open={showUpsellsConfig} onOpenChange={setShowUpsellsConfig}>
                <DialogContent className="bg-stone-800 border-stone-700 max-w-3xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-emerald-400" />
                      Configure Smart Upsells
                    </DialogTitle>
                    <p className="text-sm text-stone-400">
                      Set up which services can be suggested as add-ons during booking to increase your average ticket value.
                    </p>
                  </DialogHeader>
                  <div className="mt-4">
                    <ServiceAddonsManager 
                      services={profile?.services || []}
                      onUpdate={fetchData}
                      isPremium={user?.membership?.tier === "pro" || user?.membership?.tier === "elite" || user?.is_super_admin}
                    />
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Payment Methods Tab */}
          {activeTab === "payments" && (
            <div className="space-y-8">
              {/* Receipt Verification Section */}
              <ReceiptVerification />
              
              {/* Prepayment Settings Section */}
              <div className="border-t border-stone-700 pt-8">
                <PrepaymentSettings />
              </div>
              
              {/* Payment Method Settings */}
              <div className="border-t border-stone-700 pt-8">
                <PaymentMethodSettings stripeConnectStatus={stripeConnectStatus} />
              </div>
            </div>
          )}

          {/* Fees & Earnings Tab */}
          {activeTab === "fees" && (
            <div className="space-y-6">
              {/* Stripe Connect Setup Card - Top Priority */}
              <StripeConnectCard token={token} onStatusChange={setStripeConnectStatus} />
              
              {/* Fee Breakdown */}
              <EarningsFeeBreakdown 
                token={token}
                membershipTier={user?.membership?.tier || "free"}
                onUpgrade={() => navigate('/membership?source=fees')}
              />
            </div>
          )}

          {/* Reports Tab */}
          {activeTab === "reports" && (
            <div className="space-y-6">
              <Tabs defaultValue="earnings" className="space-y-4">
                <TabsList className="bg-stone-800/50 border border-stone-700">
                  <TabsTrigger value="earnings" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                    💰 Earnings
                  </TabsTrigger>
                  <TabsTrigger value="appointments" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                    📅 Appointments
                  </TabsTrigger>
                  <TabsTrigger value="clients" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                    👥 Client Retention
                  </TabsTrigger>
                  <TabsTrigger value="refunds" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                    💸 Refunds
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="earnings">
                  <ReportsDashboard 
                    token={token} 
                    reportType="stylist" 
                    title="Earnings Report"
                  />
                </TabsContent>
                
                <TabsContent value="appointments">
                  <ReportsDashboard 
                    token={token} 
                    reportType="stylist_appointments" 
                    title="Appointment Analytics"
                  />
                </TabsContent>
                
                <TabsContent value="clients">
                  <ReportsDashboard 
                    token={token} 
                    reportType="stylist_clients" 
                    title="Client Retention"
                  />
                </TabsContent>

                <TabsContent value="refunds">
                  <RefundsDashboard />
                </TabsContent>
              </Tabs>
            </div>
          )}

          {/* Schedule Tab */}
          {activeTab === "schedule" && (
            <div className="space-y-6">
              <Card className="bg-stone-800/50 border-stone-700">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-white">Working Hours</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {schedule.map((day, index) => (
                    <div key={day.day} className="flex items-center gap-4 p-3 bg-stone-700/30 rounded-lg">
                      <div className="w-28">
                        <span className="text-white font-medium">{day.day}</span>
                      </div>
                      <Switch
                        checked={day.enabled}
                        onCheckedChange={(checked) => {
                          const newSchedule = [...schedule];
                          newSchedule[index].enabled = checked;
                          setSchedule(newSchedule);
                        }}
                      />
                      {day.enabled && (
                        <>
                          <Input
                            type="time"
                            value={day.start_time}
                            onChange={(e) => {
                              const newSchedule = [...schedule];
                              newSchedule[index].start_time = e.target.value;
                              setSchedule(newSchedule);
                            }}
                            className="w-32 bg-stone-700 border-stone-600 text-white"
                          />
                          <span className="text-stone-400">to</span>
                          <Input
                            type="time"
                            value={day.end_time}
                            onChange={(e) => {
                              const newSchedule = [...schedule];
                              newSchedule[index].end_time = e.target.value;
                              setSchedule(newSchedule);
                            }}
                            className="w-32 bg-stone-700 border-stone-600 text-white"
                          />
                        </>
                      )}
                      {!day.enabled && <span className="text-stone-500">Closed</span>}
                    </div>
                  ))}
                  <Button 
                    onClick={saveSchedule}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-black mt-4"
                    data-testid="save-schedule-btn"
                  >
                    Save Schedule
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-stone-800/50 border-stone-700">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-white">Block Off Time</CardTitle>
                  <p className="text-sm text-stone-400">Block periods when clients cannot book</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-stone-300">Date</Label>
                      <Input
                        type="date"
                        value={newBlockedTime.date}
                        onChange={(e) => setNewBlockedTime({ ...newBlockedTime, date: e.target.value })}
                        className="mt-2 bg-stone-700 border-stone-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-300">Start</Label>
                      <Input
                        type="time"
                        value={newBlockedTime.start_time}
                        onChange={(e) => setNewBlockedTime({ ...newBlockedTime, start_time: e.target.value })}
                        className="mt-2 bg-stone-700 border-stone-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-300">End</Label>
                      <Input
                        type="time"
                        value={newBlockedTime.end_time}
                        onChange={(e) => setNewBlockedTime({ ...newBlockedTime, end_time: e.target.value })}
                        className="mt-2 bg-stone-700 border-stone-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-300">Reason</Label>
                      <Input
                        value={newBlockedTime.reason}
                        onChange={(e) => setNewBlockedTime({ ...newBlockedTime, reason: e.target.value })}
                        placeholder="e.g., Lunch break"
                        className="mt-2 bg-stone-700 border-stone-600 text-white"
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={addBlockedTime}
                    variant="outline"
                    className="border-stone-600 text-stone-300"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Blocked Time
                  </Button>

                  {blockedTimes.length > 0 && (
                    <div className="space-y-2 mt-4">
                      {blockedTimes.map((bt) => (
                        <div key={bt.id} className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                          <div className="text-sm">
                            <span className="text-white font-medium">{bt.date}</span>
                            <span className="text-stone-400 mx-2">•</span>
                            <span className="text-stone-300">{bt.start_time} - {bt.end_time}</span>
                            {bt.reason && (
                              <>
                                <span className="text-stone-400 mx-2">•</span>
                                <span className="text-stone-400">{bt.reason}</span>
                              </>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-400"
                            onClick={() => removeBlockedTime(bt.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button 
                        onClick={saveBlockedTimes}
                        className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                        data-testid="save-blocked-btn"
                      >
                        Save Blocked Times
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Rewards Command Center Tab */}
          {activeTab === "rewards" && (
            <div className="bg-white rounded-2xl p-6">
              <RewardsCommandCenter token={token} user={user} />
            </div>
          )}

          {/* Photo Gallery Tab */}
          {activeTab === "gallery" && (
            <StyleGallery token={token} membershipTier={user?.membership_tier || "free"} />
          )}

          {/* Analytics Tab */}
          {activeTab === "analytics" && (
            <div className="bg-white rounded-2xl p-6">
              <RevenueDashboard token={token} />
            </div>
          )}

          {/* Client Notes Tab */}
          {activeTab === "clients" && (
            <div className="space-y-4">
              {/* Premium Features Banner - Show for non-premium users */}
              {(user?.membership?.tier !== "pro" && user?.membership?.tier !== "elite" && !user?.is_super_admin) && (
                <div className="bg-gradient-to-r from-emerald-900/40 to-green-900/30 border border-emerald-500/30 rounded-2xl p-6">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-6 h-6 text-emerald-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-bold text-white">Unlock Pro Health Tracking</h3>
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">NEW</Badge>
                        </div>
                        <p className="text-emerald-300/70 text-sm mt-1">
                          Track scalp care, pH levels, and health profiles for personalized service
                        </p>
                      </div>
                    </div>
                    <Link to="/membership?feature=health_profiles" data-testid="upgrade-health-tracking-btn">
                      <Button className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white whitespace-nowrap">
                        <Crown className="w-4 h-4 mr-2" />
                        Upgrade to Pro
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
              <div className="bg-white rounded-2xl p-6">
                <ClientNotes token={token} membershipTier={user?.membership?.tier || "free"} isSuperAdmin={user?.is_super_admin || false} />
              </div>
            </div>
          )}

          {/* Waitlist Tab */}
          {activeTab === "waitlist" && (
            <div className="bg-white rounded-2xl p-6">
              <WaitlistManager token={token} isClient={false} />
            </div>
          )}

          {/* Marketing Tab */}
          {activeTab === "marketing" && (
            <div className="bg-white rounded-2xl p-6">
              <MarketingDashboard token={token} />
            </div>
          )}

          {/* Referrals Tab */}
          {activeTab === "referrals" && (
            <div className="bg-stone-900 rounded-2xl p-6">
              <ReferralDashboard user={user} token={token} />
            </div>
          )}

          {/* Accounting Tab (Elite Feature) */}
          {activeTab === "accounting" && (
            <div className="bg-white rounded-2xl p-6">
              <AccountingDashboard />
            </div>
          )}

          {/* Booking Widget Tab */}
          {activeTab === "widget" && (
            <div className="bg-white rounded-2xl p-6">
              <BookingWidget 
                stylistId={user?.user_id} 
                stylistName={user?.name}
              />
            </div>
          )}

          {/* Invite Clients Tab */}
          {activeTab === "invite" && (
            <ClientInviteTools 
              token={token}
              profile={profile}
              stylistId={user?.user_id}
              stylistName={user?.name || profile?.business_name}
            />
          )}

          {/* Inbox Tab */}
          {activeTab === "inbox" && (
            <Mailbox user={user} />
          )}

          {/* Client Management Tab */}
          {activeTab === "clientmgmt" && (
            <ClientManagement 
              token={token} 
              user={user}
              onUpgradeClick={() => window.location.href = '/pricing'}
            />
          )}

          {/* Reviews Tab */}
          {activeTab === "reviews" && (
            <div data-testid="reviews-tab-content">
              <div className="mb-6">
                <p className="text-gray-400">
                  See what your clients are saying about you. Respond to reviews to build stronger relationships.
                </p>
              </div>
              <ReviewsList stylistId={user?.user_id} isStylist={true} />
            </div>
          )}

          {/* Booth Management Tab (Owner Only) */}
          {activeTab === "booth" && user?.role === "owner" && (
            <div className="bg-white rounded-2xl p-6">
              <BoothManagement token={token} />
            </div>
          )}

          {/* Staff Management Tab (Owner + Elite Only) */}
          {activeTab === "staff" && user?.role === "owner" && (
            <StaffManagement token={token} />
          )}

          {/* Pro Mini-Site Settings Tab */}
          {activeTab === "minisite" && (
            <div className="bg-white rounded-2xl p-6">
              {/* Debug: Log user info */}
              {console.log("StylistDashboard user:", user?.email, user?.membership?.tier)}
              <MiniSiteSettings 
                token={token} 
                membershipTier={user?.membership?.tier || "free"}
                userName={user?.name || ""}
                userEmail={user?.email || ""}
              />
            </div>
          )}

          {/* Beta Analytics Tab (Owner Only) */}
          {activeTab === "beta_analytics" && user?.role === "owner" && (
            <BetaAnalyticsDashboard />
          )}

          {/* Display Settings Tab */}
          {activeTab === "display" && (
            <DisplaySettings />
          )}
        </div>
      </main>

      {/* Get Started Guide */}
      {showGetStartedGuide && (
        <GetStartedGuide
          userRole={user?.role === 'owner' ? 'owner' : 'stylist'}
          onComplete={() => setShowGetStartedGuide(false)}
          onSkip={() => setShowGetStartedGuide(false)}
        />
      )}

      {/* Help Button - Re-open Guide */}
      <Button
        onClick={() => setShowGetStartedGuide(true)}
        className="fixed bottom-24 left-6 lg:bottom-6 lg:left-72 bg-stone-800 hover:bg-stone-700 text-white shadow-lg rounded-full w-12 h-12 p-0 z-40 touch-target btn-ripple"
        title="Show Get Started Guide"
        data-testid="help-guide-btn"
      >
        <HelpCircle className="w-5 h-5" />
      </Button>

      {/* Floating User Guide Button */}
      <Link
        to="/user-guide?role=stylist"
        className="fixed bottom-40 right-6 bg-violet-600 hover:bg-violet-700 text-white shadow-lg rounded-full w-12 h-12 flex items-center justify-center z-40 transition-all hover:scale-110"
        title="User Guide"
        data-testid="floating-guide-btn"
      >
        <BookOpen className="w-5 h-5" />
      </Link>

      {/* AI Chat Button */}
      <button
        onClick={() => setChatOpen(!chatOpen)}
        className="fixed bottom-24 right-6 bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg rounded-full w-14 h-14 flex items-center justify-center z-40 hover:scale-110 transition-all"
        data-testid="ai-chat-fab"
      >
        <MessageSquare className="w-6 h-6" />
      </button>

      {/* AI Chatbot */}
      {chatOpen && <AIChatbot onClose={() => setChatOpen(false)} userRole="stylist" />}

      {/* Refund Modal */}
      <RefundModal
        isOpen={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          setSelectedAppointmentForRefund(null);
        }}
        appointment={selectedAppointmentForRefund}
        token={token}
        onRefundComplete={() => {
          fetchData(); // Refresh appointments
          setShowRefundModal(false);
          setSelectedAppointmentForRefund(null);
        }}
      />

      {/* Trial Expired Modal */}
      <TrialExpiredModal
        isOpen={showTrialExpiredModal}
        onClose={() => setShowTrialExpiredModal(false)}
        previousTier={user?.membership?.tier || "pro"}
        onContinueFree={handleTrialExpiredContinueFree}
      />

      {/* Soldier Recruitment Playbook */}
      <SoldierRecruitmentPlaybook 
        isOpen={showPlaybook}
        onClose={() => setShowPlaybook(false)}
        referralCode={user?.soldier_referral_code || 'YOUR-CODE'}
        discountValue="14 days FREE + $10 off for 3 months"
      />
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav userRole={user?.role || "stylist"} />
    </div>
  );
};

export default StylistDashboard;
